import * as React from 'react';
import { Button } from '@/components/forms/ui/button';
import { cn } from '@/lib/utils';

type Option<Value extends string | number> = {
  value: Value;
  label: string;
};

type ButtonSelectProps<Value extends string | number> = {
  id?: string;
  label: string;
  value: Value | null | undefined;
  options: ReadonlyArray<Option<Value>>;
  onChange: (value: Value | undefined) => void;
  onBlur?: () => void;
  placeholder?: string;
  description?: React.ReactNode;
  error?: string;
  disabled?: boolean;
  allowDeselect?: boolean;
};

export function ButtonSelect<Value extends string | number>({
  id,
  label,
  value,
  options,
  onChange,
  onBlur,
  placeholder,
  description,
  error,
  disabled = false,
  allowDeselect = false
}: ButtonSelectProps<Value>) {
  const handleClick = (next: Value) => {
    const selected = allowDeselect && value === next ? undefined : next;
    onChange(selected);
  };
  return (
    <div className="flex flex-col gap-1" id={id}>
      <label className="text-sm font-medium">{label}</label>
      {description ? <span className="text-muted-foreground text-xs">{description}</span> : null}
      {disabled ? (
        <div className="rounded border-[1px] border-gray-300 bg-gray-200 px-2 py-1.5 text-sm text-gray-400">
          {placeholder ?? ''}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2" onBlur={onBlur}>
          {options.map(opt => {
            const isActive = opt.value === value;
            return (
              <Button
                key={opt.value}
                type="button"
                variant={isActive ? 'default' : 'outline'}
                onClick={() => handleClick(opt.value)}
                aria-pressed={isActive}
                className={cn(
                  'h-9 border px-2 font-light',
                  isActive ? 'border-transparent' : 'border-input'
                )}>
                {opt.label}
              </Button>
            );
          })}
        </div>
      )}
      {error ? <span className="text-destructive text-sm">{error}</span> : null}
    </div>
  );
}
